import { useMutation, useQueryClient } from "@tanstack/react-query";

import { errorHandler } from "#app/monitoring/errorHandler";
import { showErrorToast } from "#app/notifications";
import {
  getOnboardingProcessStepsQueryData,
  onboardingProcessStepsQuery,
  setOnboardingProcessStepsQueryData,
} from "#modules/onboardingV2/infra/onboardingProcessSteps.query";
import { updateOnboardingProcessStepApi } from "#modules/onboardingV2/infra/updateOnboardingProcessStep.api";

export const useUpdateOnboardingProcessStep = () => {
  const queryClient = useQueryClient();

  const { mutate, ...rest } = useMutation({
    mutationFn: updateOnboardingProcessStepApi,
    onMutate: async (variables) => {
      await queryClient.cancelQueries({ queryKey: onboardingProcessStepsQuery().queryKey });
      const previous = getOnboardingProcessStepsQueryData(queryClient);

      if (previous) {
        setOnboardingProcessStepsQueryData(
          queryClient,
          previous.map((processStep) =>
            processStep.id === variables.id
              ? { ...processStep, ...variables.payload }
              : processStep,
          ),
        );
      }

      return { previous };
    },
    onSuccess: (data, variables) => {
      // A row that crosses between CompanyMachine/CompanySurfaceTreatment comes back with a new
      // id, so replace by the original (pre-update) id rather than matching on data.id.
      const current = getOnboardingProcessStepsQueryData(queryClient);

      if (current) {
        setOnboardingProcessStepsQueryData(
          queryClient,
          current.map((processStep) => (processStep.id === variables.id ? data : processStep)),
        );
      }
    },
    onError: (err: Error, _variables, context) => {
      showErrorToast({ title: err.message });
      errorHandler(err);

      if (context?.previous) {
        setOnboardingProcessStepsQueryData(queryClient, context.previous);
      }
    },
    onSettled: () => {
      void queryClient.invalidateQueries(onboardingProcessStepsQuery());
    },
  });

  return { updateOnboardingProcessStep: mutate, ...rest };
};
